// The contract wording behind a step: its references, each one click from the words.
import { Fragment, useState } from "react";

import { CLAUSES, NEED_CLAUSES } from "./contractClauses";
import { inGroups, needCategory } from "./needs";
import { type PathStep, STEPS, type StepLimit, type StepNeed } from "./pathSteps";
import styles from "./amazonOverview.module.css";

/** A note some saved needs still carry in place of a clause; the page no longer shows it. */
const NOT_IN_CONTRACTS = "Not in the contracts; only Amazon has it";

export const ContractText = ({ ids, more }: { ids: string[]; more: string }) => {
  const [open, setOpen] = useState(false);
  if (!ids.length) return null;
  return (
    <>
      <button
        type="button"
        className={styles.moreLink}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {open ? "Hide the contract text" : more}
      </button>
      {open &&
        ids.map((id) => (
          <div key={id} className={styles.clause}>
            <div className={styles.cardRef}>{CLAUSES[id].label}</div>
            <blockquote className={styles.verbatim}>
              {CLAUSES[id].text}
            </blockquote>
          </div>
        ))}
    </>
  );
};

export type OnlyWhere = NonNullable<StepNeed["only"]>;

const ONLY_LABEL: Record<OnlyWhere, string> = {
  existingPv: "Only sites with existing PV",
  skylights: "Only roofs with skylights",
};

/** The page's own needs only some buildings need, by their words: a need edited on the
 * page is saved as words and a clause, so it is matched back by its words. */
const ONLY_BY_TEXT = new Map(
  STEPS.flatMap((s) => s.needs.flatMap((n) => (n.only ? [[n.text, n.only] as const] : []))),
);

export const OnlyTag = ({ only }: { only: OnlyWhere }) => (
  <span className={styles.onlyTag}>{ONLY_LABEL[only]}</span>
);

const NeedItem = ({ need }: { need: StepNeed }) => {
  const only = need.only ?? ONLY_BY_TEXT.get(need.text);
  return (
    <li>
      {only && (
        <>
          <OnlyTag only={only} />{" "}
        </>
      )}
      {need.text}{" "}
      {need.source && need.source !== NOT_IN_CONTRACTS && (
        <span className={styles.source}>{need.source}</span>
      )}{" "}
      <ContractText ids={NEED_CLAUSES[need.source] ?? []} more="see more" />
    </li>
  );
};

/** What a step needs from Amazon, each with the clause behind it, under the categories
 * Step 3 groups the step's items by (Noaa, 20 Oct 2026), numbered on across them. */
export const NeedsList = ({ step }: { step: PathStep }) => {
  const groups = inGroups(step.needs, (n) => needCategory(step.id, n));
  let start = 1;
  return (
    <>
      {groups.map(([category, needs]) => {
        const first = start;
        start += needs.length;
        return (
          <Fragment key={category}>
            {category && (
              <p className={styles.needCategory}>
                {category} ({needs.length})
              </p>
            )}
            <ol start={first}>
              {needs.map((n) => (
                <NeedItem key={n.text} need={n} />
              ))}
            </ol>
          </Fragment>
        );
      })}
    </>
  );
};

/** The time the contract gives a step, and its own words one click away. */
export const LimitBox = ({ limit }: { limit: StepLimit }) => {
  const [verbatim, setVerbatim] = useState(false);
  return (
    <div className={limit.risk ? styles.limitBoxRisk : styles.limitBox}>
      <span className={styles.limitHead}>{limit.label}</span>
      <span>{limit.clause}</span>
      <span className={styles.source}>{limit.reference}</span>
      <button
        type="button"
        className={styles.moreLink}
        aria-expanded={verbatim}
        onClick={() => setVerbatim((v) => !v)}
      >
        {verbatim ? "Hide the contract text" : "Show the contract text"}
      </button>
      {verbatim && <blockquote className={styles.verbatim}>{limit.text}</blockquote>}
    </div>
  );
};
